import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, AlertCircle, ChevronDown, RotateCcw } from 'lucide-react';
import { usePageTitle, Avatar } from '../components/AppShell';
import { useLang } from '../lib/lang';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { T } from '../lib/tokens';

interface Props { user: AuthUser }
interface SchoolClass { id:string; name:string; level:string; school_id:string }
interface StudentRow {
  id:string; registration_number:string; full_name:string;
  ic_number:string|null; record_id:string|null; mark:string|null;
}
interface SessionStudents {
  session:{ id:string; date:string; class_id:string; status:string };
  students:StudentRow[];
}
type AttMark = 'PRESENT'|'LATE'|'ABSENT_SICK'|'ABSENT_EXCUSED'|'ABSENT_UNEXCUSED'|null;

const C = T.color;

const MARKS = [
  { key:'PRESENT'          as const, ms:'Hadir',      en:'Present', activeBg:C.success },
  { key:'LATE'             as const, ms:'Lewat',       en:'Late',    activeBg:C.warning },
  { key:'ABSENT_UNEXCUSED' as const, ms:'Tidak Hadir', en:'Absent',  activeBg:C.danger  },
];

export function AttendancePage({ user }: Props) {
  const { lang } = useLang();
  const navigate  = useNavigate();
  const E = lang==='en';
  usePageTitle(E?'Attendance':'Kehadiran');

  const [classes,    setClasses]    = useState<SchoolClass[]>([]);
  const [selClass,   setSelClass]   = useState('');
  const [date,       setDate]       = useState(()=>new Date().toISOString().slice(0,10));
  const [period,     setPeriod]     = useState(1);
  const [session,    setSession]    = useState<{id:string;date:string;class_id:string;status:string}|null>(null);
  const [students,   setStudents]   = useState<StudentRow[]>([]);
  const [marks,      setMarks]      = useState<Record<string,AttMark>>({});
  const [lateMin,    setLateMin]    = useState<Record<string,string>>({});
  const [notes,      setNotes]      = useState<Record<string,string>>({});
  const [openNote,   setOpenNote]   = useState<string|null>(null);
  const [loading,    setLoading]    = useState(false);
  const [starting,   setStarting]   = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState<string|null>(null);
  const [submitted,  setSubmitted]  = useState(false);

  const loadClasses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet<SchoolClass[]>('/api/students/classes');
      setClasses(data);
      if (data.length>0 && !selClass) setSelClass(data[0]!.id);
    } catch(e) { setError(e instanceof Error?e.message:'Error loading classes'); }
    finally { setLoading(false); }
  },[selClass]);

  useEffect(()=>{ void loadClasses(); },[]);

  async function startSession() {
    if (!selClass) return;
    setStarting(true); setError(null);
    try {
      const sess = await apiPost<{id:string}>('/api/students/sessions',{ classId:selClass, date, periodNumber:period });
      const data  = await apiGet<SessionStudents>(`/api/students/sessions/${sess.id}/students`);
      setSession(data.session);
      setStudents(data.students);
      const initMarks: Record<string,AttMark> = {};
      data.students.forEach(s=>{ initMarks[s.id]=(s.mark as AttMark)||null; });
      setMarks(initMarks);
      setSubmitted(false);
    } catch(e){ setError(e instanceof Error?e.message:'Error starting session'); }
    finally { setStarting(false); }
  }

  function setMark(id:string, m:AttMark) { setMarks(p=>({...p,[id]:m})); }
  function markAll(m:AttMark) { const n:Record<string,AttMark>={}; students.forEach(s=>{n[s.id]=m;}); setMarks(n); }

  async function handleSubmit() {
    if (!session) return;
    const unmarked = students.filter(s=>!marks[s.id]);
    if (unmarked.length>0) {
      const ok = window.confirm(E
        ?`${unmarked.length} students are still unmarked. Submit anyway?`
        :`${unmarked.length} pelajar masih belum ditandakan. Hantar tetap?`);
      if (!ok) return;
    }
    setSaving(true); setError(null);
    try {
      await apiPost(`/api/students/sessions/${session.id}/submit`,{
        records:students.map(s=>({
          studentId:s.id,
          mark:marks[s.id]??'ABSENT_UNEXCUSED',
          checkInMethod:'MANUAL',
        }))
      });
      setSubmitted(true);
    } catch(e){ setError(e instanceof Error?e.message:'Error submitting'); }
    finally { setSaving(false); }
  }

  const presentCount = students.filter(s=>marks[s.id]==='PRESENT').length;
  const lateCount    = students.filter(s=>marks[s.id]==='LATE').length;
  const absentCount  = students.filter(s=>marks[s.id]?.startsWith('ABSENT')).length;
  const markedCount  = students.filter(s=>!!marks[s.id]).length;
  const total        = students.length;
  const pct          = total>0?Math.round((markedCount/total)*100):0;
  const allMarked    = markedCount===total && total>0;
  const selClassName = classes.find(c=>c.id===selClass)?.name??'';

  // ── Submitted success ──────────────────────────────────────────────────────
  if (submitted) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'60vh', flexDirection:'column', gap:'16px', textAlign:'center' }}>
      <div style={{ width:'80px', height:'80px', borderRadius:'50%', background:C.successLight, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <CalendarCheck size={36} color={C.success} strokeWidth={2}/>
      </div>
      <h2 style={{ fontFamily:T.font.serif, fontSize:'24px', fontWeight:700, color:C.textPrimary }}>
        {E?'Attendance Submitted':'Kehadiran Berjaya Dihantar'}
      </h2>
      <p style={{ fontSize:'14px', color:C.textSecondary, maxWidth:'380px', lineHeight:1.6 }}>
        {selClassName} · {presentCount} {E?'present':'hadir'}, {lateCount} {E?'late':'lewat'}, {absentCount} {E?'absent':'tidak hadir'}
      </p>
      <div style={{ display:'flex', gap:'12px', marginTop:'8px' }}>
        <button
          onClick={()=>{ setSession(null); setStudents([]); setMarks({}); setSubmitted(false); }}
          style={{ background:C.royal, color:C.midnight, border:'none', borderRadius:'8px', padding:'12px 24px', fontSize:'14px', fontWeight:700, cursor:'pointer', fontFamily:T.font.sans }}
        >
          {E?'Mark Another Class':'Tandai Kelas Lain'}
        </button>
        <button
          onClick={()=>navigate('/admin/attendance/alerts')}
          style={{ background:'none', border:`1.5px solid ${C.border}`, borderRadius:'8px', padding:'12px 24px', fontSize:'14px', fontWeight:600, cursor:'pointer', fontFamily:T.font.sans, color:C.textPrimary }}
        >
          {E?'View Alerts':'Lihat Makluman'}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth:'820px' }}>

      {/* ── Step 1: Class selector ──────────────────────────────────────── */}
      {!session && (
        <div style={{ background:C.card, borderRadius:'12px', border:`1px solid ${C.border}`, padding:'28px', boxShadow:T.shadow.sm }}>
          <h2 style={{ fontFamily:T.font.serif, fontSize:'20px', fontWeight:600, color:C.textPrimary, marginBottom:'20px' }}>
            {E?'Select Class & Date':'Pilih Kelas & Tarikh'}
          </h2>

          <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:'20px', alignItems:'end', marginBottom:'20px' }}>
            <div>
              <label style={{ display:'block', fontSize:'11px', fontWeight:700, color:C.textSecondary, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'6px' }}>
                {E?'Class':'Kelas'} *
              </label>
              <div style={{ position:'relative' }}>
                <select
                  value={selClass}
                  onChange={e=>setSelClass(e.target.value)}
                  disabled={loading}
                  style={{
                    width:'100%', appearance:'none',
                    background:C.card, border:`1px solid ${C.borderStrong}`,
                    borderRadius:'6px', padding:'11px 40px 11px 14px',
                    fontSize:'14px', color:C.textPrimary,
                    fontFamily:T.font.sans, cursor:'pointer', outline:'none',
                  }}
                >
                  {loading && <option>{E?'Loading…':'Memuat…'}</option>}
                  {classes.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <ChevronDown size={14} style={{ position:'absolute', right:'14px', top:'50%', transform:'translateY(-50%)', color:C.textDisabled, pointerEvents:'none' }}/>
              </div>
            </div>

            <div>
              <label style={{ display:'block', fontSize:'11px', fontWeight:700, color:C.textSecondary, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'6px' }}>
                {E?'Date':'Tarikh'} *
              </label>
              <input
                type="date" value={date}
                onChange={e=>setDate(e.target.value)}
                style={{
                  background:C.card, border:`1px solid ${C.borderStrong}`,
                  borderRadius:'6px', padding:'11px 14px',
                  fontSize:'14px', color:C.textPrimary,
                  fontFamily:T.font.sans, outline:'none',
                }}
              />
            </div>
          </div>

          {error && (
            <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'10px 14px', background:C.dangerLight, border:`1px solid ${C.danger}44`, borderRadius:'8px', marginBottom:'16px' }}>
              <AlertCircle size={14} color={C.danger}/>
              <span style={{ fontSize:'13px', color:C.danger }}>{error}</span>
            </div>
          )}

          <button
            onClick={()=>void startSession()}
            disabled={!selClass || starting || loading}
            style={{
              background:C.royal, color:C.midnight, border:'none',
              borderRadius:'8px', padding:'12px 28px',
              fontSize:'14px', fontWeight:700, cursor:starting?'not-allowed':'pointer',
              opacity:starting?0.8:1, fontFamily:T.font.sans,
              display:'flex', alignItems:'center', gap:'8px',
              transition:'opacity 0.15s',
            }}
          >
            {starting ? <><RotateCcw size={15}/>{E?'Starting…':'Memulakan…'}</> : <><CalendarCheck size={15}/>{E?'Start Marking Attendance':'Mula Rekod Kehadiran'}</>}
          </button>
        </div>
      )}

      {/* ── Step 2: Student list ──────────────────────────────────────── */}
      {session && (
        <>
          {/* Header row */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px', flexWrap:'wrap', gap:'10px' }}>
            <div>
              <h2 style={{ fontFamily:T.font.serif, fontSize:'22px', fontWeight:700, color:C.textPrimary, marginBottom:'2px' }}>
                {selClassName}
              </h2>
              <p style={{ fontSize:'13px', color:C.textSecondary }}>
                {new Date(date+'T00:00:00').toLocaleDateString(E?'en-GB':'ms-BN',{ weekday:'long', year:'numeric', month:'long', day:'numeric' })}
              </p>
            </div>
            <div style={{ display:'flex', gap:'8px' }}>
              <button
                onClick={()=>markAll('PRESENT')}
                style={{ background:C.successLight, color:C.success, border:`1px solid ${C.success}55`, borderRadius:'6px', padding:'7px 14px', fontSize:'12px', fontWeight:600, cursor:'pointer', fontFamily:T.font.sans }}
              >
                ✓ {E?'All Present':'Semua Hadir'}
              </button>
              <button
                onClick={()=>{ setSession(null); setStudents([]); setMarks({}); }}
                style={{ background:C.borderSubtle, color:C.textSecondary, border:`1px solid ${C.border}`, borderRadius:'6px', padding:'7px 14px', fontSize:'12px', fontWeight:600, cursor:'pointer', fontFamily:T.font.sans }}
              >
                {E?'Change Class':'Tukar Kelas'}
              </button>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ background:C.card, borderRadius:'10px', border:`1px solid ${C.border}`, padding:'16px', marginBottom:'16px', boxShadow:T.shadow.xs }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
              <span style={{ fontSize:'13px', fontWeight:600, color:C.textPrimary }}>
                {E?'Progress':'Kemajuan'}: {markedCount} / {total}
              </span>
              <span style={{ fontSize:'13px', fontWeight:700, color:pct===100?C.success:C.textSecondary }}>{pct}%</span>
            </div>
            <div style={{ height:'8px', background:C.borderSubtle, borderRadius:'999px', overflow:'hidden' }}>
              <div style={{ height:'100%', borderRadius:'999px', background:pct===100?C.success:C.royal, width:`${pct}%`, transition:'width 0.35s ease' }}/>
            </div>
          </div>

          {/* Student cards */}
          <div style={{ background:C.card, borderRadius:'12px', border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:T.shadow.sm }}>
            {students.map((s,i)=>{
              const m = marks[s.id]??null;
              const rowBg = m==='PRESENT'?'#FAFDFC':m?.startsWith('ABSENT')?'#FFFBFB':m==='LATE'?'#FFFEF5':C.card;

              return (
                <div key={s.id} style={{ borderBottom:i<students.length-1?`1px solid ${C.borderSubtle}`:'none', background:rowBg, transition:'background 0.12s' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'12px', padding:'14px 16px' }}>
                    {/* Avatar */}
                    <Avatar email={s.full_name} size={42}/>

                    {/* Name + ID */}
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ fontSize:'14px', fontWeight:600, color:C.textPrimary, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:'2px' }}>
                        {s.full_name}
                      </p>
                      <p style={{ fontSize:'11px', color:C.textSecondary, fontFamily:T.font.mono }}>{s.registration_number}</p>
                    </div>

                    {/* Mark buttons */}
                    <div style={{ display:'flex', gap:'5px', flexShrink:0 }}>
                      {MARKS.map((mk)=>{
                        const active = m===mk.key;
                        return (
                          <button
                            key={mk.key}
                            onClick={()=>setMark(s.id, active?null:mk.key)}
                            style={{
                              padding:'7px 13px',
                              background: active ? mk.activeBg : 'transparent',
                              color: active ? '#FFFFFF' : '#9CA3AF',
                              border: `1.5px solid ${active ? mk.activeBg : '#E5E7EB'}`,
                              borderRadius:'999px',
                              fontSize:'12px', fontWeight: active ? 700 : 500,
                              cursor:'pointer', fontFamily:T.font.sans,
                              transition:'all 0.1s ease',
                              whiteSpace:'nowrap',
                            }}
                          >
                            {E?mk.en:mk.ms}
                          </button>
                        );
                      })}
                    </div>

                    {/* Note toggle */}
                    <button
                      onClick={()=>setOpenNote(openNote===s.id?null:s.id)}
                      title={E?'Add note':'Tambah nota'}
                      style={{ background:'none', border:'none', cursor:'pointer', color:notes[s.id]?C.royal:'#D1D5DB', fontSize:'18px', padding:'4px', transition:'color 0.1s', flexShrink:0 }}
                    >
                      {notes[s.id]?'📝':'💬'}
                    </button>
                  </div>

                  {/* Late minutes */}
                  {m==='LATE' && (
                    <div style={{ paddingLeft:'70px', paddingBottom:'12px', paddingRight:'16px', display:'flex', alignItems:'center', gap:'8px' }}>
                      <label style={{ fontSize:'12px', color:C.textSecondary }}>{E?'Minutes late:':'Minit lewat:'}</label>
                      <input
                        type="number" min={1} max={60} value={lateMin[s.id]??'5'}
                        onChange={e=>setLateMin(p=>({...p,[s.id]:e.target.value}))}
                        style={{ width:'60px', background:'#FFFBEB', border:`1px solid ${C.warning}77`, borderRadius:'6px', padding:'4px 8px', fontSize:'13px', color:C.warning, fontWeight:600, fontFamily:T.font.mono, textAlign:'center', outline:'none' }}
                      />
                    </div>
                  )}

                  {/* Note input */}
                  {openNote===s.id && (
                    <div style={{ paddingLeft:'70px', paddingBottom:'12px', paddingRight:'16px' }}>
                      <textarea
                        value={notes[s.id]??''}
                        onChange={e=>setNotes(p=>({...p,[s.id]:e.target.value}))}
                        placeholder={E?'Teacher note (optional)…':'Nota guru (pilihan)…'}
                        rows={2}
                        style={{ width:'100%', background:'#F9FAFB', border:`1px solid ${C.border}`, borderRadius:'6px', padding:'8px 10px', fontSize:'12px', color:C.textPrimary, fontFamily:T.font.sans, outline:'none', resize:'vertical' }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Sticky footer ── */}
          <div style={{ position:'sticky', bottom:0, background:'rgba(250,248,242,0.95)', backdropFilter:'blur(8px)', borderTop:`1px solid ${C.border}`, paddingTop:'14px', marginTop:'16px', zIndex:50 }}>
            <div style={{ display:'flex', gap:'16px', marginBottom:'10px', flexWrap:'wrap', fontSize:'13px' }}>
              <span style={{ color:C.textSecondary, fontWeight:500 }}>{total} {E?'students':'pelajar'}</span>
              <span style={{ color:C.success, fontWeight:600 }}>✓ {presentCount} {E?'present':'hadir'}</span>
              <span style={{ color:C.warning, fontWeight:600 }}>⏱ {lateCount} {E?'late':'lewat'}</span>
              <span style={{ color:C.danger, fontWeight:600 }}>✗ {absentCount} {E?'absent':'tidak hadir'}</span>
              {total-markedCount>0 && <span style={{ color:C.textDisabled }}>○ {total-markedCount} {E?'unmarked':'belum ditanda'}</span>}
            </div>

            {error && <p style={{ fontSize:'12px', color:C.danger, marginBottom:'8px' }}>{error}</p>}

            <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end' }}>
              <button style={{ background:'none', border:`1.5px solid ${C.border}`, borderRadius:'8px', padding:'10px 20px', fontSize:'13px', fontWeight:600, cursor:'pointer', color:C.textPrimary, fontFamily:T.font.sans }}>
                {E?'Save Draft':'Simpan Draf'}
              </button>
              <button
                onClick={()=>void handleSubmit()}
                disabled={saving}
                style={{
                  background: allMarked ? C.royal : C.warning,
                  color: allMarked ? C.midnight : '#FFFFFF',
                  border:'none', borderRadius:'8px',
                  padding:'10px 24px', fontSize:'13px', fontWeight:700,
                  cursor:saving?'not-allowed':'pointer',
                  fontFamily:T.font.sans, opacity:saving?0.8:1,
                  transition:'all 0.15s',
                  display:'flex', alignItems:'center', gap:'6px',
                }}
              >
                {saving
                  ?(E?'Submitting…':'Menghantar…')
                  :allMarked
                    ?(E?'Submit Attendance ▶':'Hantar Kehadiran ▶')
                    :(E?`Submit (${total-markedCount} unmarked)`:`Hantar (${total-markedCount} belum)`)}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
